import { ReactNode } from 'react';
import { formatValue } from '../../glossary/formatValue';
import { TooltipLabel } from '../TooltipLabel';
import { formatField, inferFormat, isEmpty } from './format';
import { EnvRow, FieldSpec } from './types';

export function FieldGrid({ fields, dense = true }: { fields: FieldSpec[]; dense?: boolean }) {
  const gap = dense ? 'gap-x-6 gap-y-2' : 'gap-x-8 gap-y-3';
  return (
    <dl className={`grid grid-cols-2 ${gap} md:grid-cols-3 lg:grid-cols-4`}>
      {fields.map((f, i) => {
        const format = f.format ?? inferFormat(f.value);
        let display: ReactNode;
        if (isEmpty(f.value)) display = <span className="text-gray-400">—</span>;
        else display = formatField(f.value, format);
        return (
          <div key={i} className="flex flex-col">
            <dt className="text-[10px] font-medium uppercase tracking-wide text-gray-600">
              {f.tooltip ? <TooltipLabel label={f.label} tooltip={f.tooltip} /> : f.label}
            </dt>
            <dd className="text-sm font-semibold text-gray-900">{display}</dd>
          </div>
        );
      })}
    </dl>
  );
}

export function PerEnvTable({ rows }: { rows: EnvRow[] }) {
  const populated = rows.filter(r => !isEmpty(r.daily) || !isEmpty(r.inflation));
  if (populated.length === 0) return null;
  return (
    <table className="w-full max-w-md text-xs">
      <thead>
        <tr className="border-b border-gray-300 text-left text-[10px] uppercase tracking-wide text-gray-600">
          <th className="py-1 pr-3 font-medium">Care setting</th>
          <th className="py-1 pr-3 font-medium">Daily benefit</th>
          <th className="py-1 font-medium">Inflation %</th>
        </tr>
      </thead>
      <tbody>
        {populated.map(r => (
          <tr key={r.env} className="border-b border-gray-100 last:border-b-0">
            <td className="py-1 pr-3 text-gray-800">{r.env}</td>
            <td className="py-1 pr-3 font-semibold text-gray-900">
              {isEmpty(r.daily) ? (
                <span className="text-gray-400">—</span>
              ) : (
                formatValue(r.daily, 'currency')
              )}
            </td>
            <td className="py-1 font-semibold text-gray-900">
              {isEmpty(r.inflation) ? (
                <span className="text-gray-400">—</span>
              ) : (
                formatValue(r.inflation, 'percent')
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
